import React, { Suspense, useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import {
  Phone, Menu, ChevronDown, Mail,
  Facebook, Instagram, Linkedin, ShieldCheck,
  MessageSquare, BadgePercent, X, Calculator, ArrowRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetClose, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { Separator } from "@/components/ui/separator";
import { Toaster } from "@/components/ui/sonner";
import { COMPANY, OFFICES, PROMO, FAMILY_BRANDS, SOCIAL_LINKS } from "@/data/company";
import { FinancingLink } from "@/components/site/FinancingLink";
import { RouteFallback } from "@/components/site/RouteFallback";
import { JsonLd, PLUMBER_SCHEMA } from "@/components/site/SEO";
import { MainNav } from "@/components/site/nav/MainNav";
import { MobileNav } from "@/components/site/nav/MobileNav";
import { YelpIcon } from "@/components/site/nav/YelpIcon";
import { PROMO_FINANCING_LINE } from "@/data/financing";
import { cityLinksByCounty, NAV_LOCATIONS } from "@/data/nav";
import { ReviewsProvider } from "@/components/site/reviews/ReviewsContext";

const SOCIAL_ICONS = { facebook: Facebook, instagram: Instagram, linkedin: Linkedin, yelp: YelpIcon };
// Light indexes only (slug/name) — the full city + service copy stays out of the shared shell bundle.
import { SERVICE_INDEX as SERVICES } from "@/data/services-index";
import { CITY_INDEX as CITIES, COUNTIES } from "@/data/cities-index";
import { devWarn } from "@/lib/logger";
import { useScrollDirection } from "@/hooks/use-scroll-direction";
import logoNavy from "@/assets/idb-logo.png";
import logoWhite from "@/assets/idb-logo-white.png";

export const Logo = ({ dark = false, compactOnDesktop = false }) => (
  <Link to="/" className="flex min-h-11 items-center gap-3 shrink-0" data-testid="header-logo" aria-label="IDB Plumbing Solutions home">
    <img
      src={dark ? logoWhite : logoNavy}
      alt="IDB Plumbing logo"
      width={225}
      height={176}
      className="h-9 w-auto sm:h-11"
    />
    <span className={`hidden border-l pl-3 text-[10px] font-semibold uppercase leading-tight tracking-[0.22em] sm:block ${compactOnDesktop ? "lg:hidden" : ""} ${dark ? "border-white/20 text-slate-300" : "border-border text-slate-500"}`}>
      Solutions<br />
      <span className={dark ? "text-slate-400" : "text-slate-400"}>Washington State</span>
    </span>
  </Link>
);

// Phones (<768px) skip this strip: the sticky header's phone button carries the
// "24/7" label there, which saves a third of the top chrome on an 812px screen.
const EmergencyBar = () => (
  <div className="band-blueprint relative hidden overflow-hidden border-b border-white/10 bg-[#0A1628] text-white md:block" data-testid="emergency-topbar">
    <div className="mx-auto flex max-w-7xl items-center justify-center gap-2 px-4 py-2 text-center sm:justify-between sm:px-6 lg:px-8">
      <div className="hidden items-center gap-4 sm:flex">
        <p className="font-condensed text-xs font-medium uppercase tracking-[0.16em] text-slate-300">
          Licensed, Bonded &amp; Insured · {COMPANY.license}
        </p>
        <span className="h-3 w-px bg-white/15" aria-hidden="true" />
        <Link
          to="/estimate"
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-blue-300 transition-colors hover:text-white"
          data-testid="emergency-topbar-estimate-link"
        >
          <Calculator className="h-3.5 w-3.5" aria-hidden="true" />
          Free 60-second cost estimate
          <ArrowRight className="h-3 w-3" aria-hidden="true" />
        </Link>
      </div>
      <a
        href={COMPANY.phoneHref}
        className="tap-target inline-flex items-center gap-2 whitespace-nowrap text-xs font-semibold text-white transition-colors hover:text-blue-300"
        data-testid="emergency-topbar-call-button"
      >
        <span className="relative flex h-2 w-2" aria-hidden="true">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-blue-400 opacity-60" />
          <span className="relative inline-flex h-2 w-2 rounded-full bg-blue-400" />
        </span>
        {/* Phones get one short line; the full sentence returns from the sm: breakpoint. */}
        <span className="sm:hidden">24/7 Emergency · Call {COMPANY.phone}</span>
        <span className="hidden sm:inline">24/7 Emergency Plumbing Service — Call Now {COMPANY.phone}</span>
      </a>
    </div>
  </div>
);

const DesktopActions = () => (
  <div className="hidden items-center gap-3 lg:flex">
    {/* lg shows the phone icon only; the number is visible as text from xl (1280px) up. */}
    <a
      href={COMPANY.phoneHref}
      aria-label={`Call IDB Plumbing Solutions, ${COMPANY.phone}`}
      className="inline-flex min-h-11 min-w-11 items-center justify-center gap-2 whitespace-nowrap text-sm font-semibold text-[#0E1F3A] hover:text-[#1D4ED8] xl:min-w-0 xl:justify-start"
      data-testid="header-phone-link"
    >
      <Phone className="h-4 w-4 text-[#1D4ED8]" aria-hidden="true" />
      <span className="hidden xl:inline">{COMPANY.phone}</span>
    </a>
    <Button asChild className="btn-glow bg-[#1D4ED8] px-3 font-semibold text-white transition-transform hover:-translate-y-px hover:bg-[#1E40AF] active:translate-y-0 xl:px-4" data-testid="header-request-service-button">
      <Link to="/contact">Request Service</Link>
    </Button>
  </div>
);

const MobileActions = ({ open, onOpenChange, pathname }) => (
  <div className="flex items-center gap-2 lg:hidden">
    <a
      href={COMPANY.phoneHref}
      aria-label={`Call IDB Plumbing Solutions, 24/7 emergency line, ${COMPANY.phone}`}
      className="flex h-11 w-11 flex-col items-center justify-center gap-0.5 rounded-lg bg-[#1D4ED8] text-white active:bg-[#1E40AF]"
      data-testid="header-mobile-call-button"
    >
      <Phone className="h-4 w-4" aria-hidden="true" />
      {/* Replaces the emergency strip's "24/7" on phones; tablets still show the strip.
          font-semibold: Plex Condensed ships in 500/600 only, so this is the weight that rendered anyway (batch 3, Part 4). */}
      <span className="font-condensed text-[9px] font-semibold uppercase leading-none tracking-wide md:hidden" aria-hidden="true" data-testid="header-mobile-call-247">24/7</span>
    </a>
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetTrigger asChild>
        <Button variant="outline" size="icon" className="h-11 w-11 border-border" aria-label="Open menu" data-testid="mobile-menu-button">
          <Menu className="h-5 w-5" aria-hidden="true" />
        </Button>
      </SheetTrigger>
      <SheetContent
        side="right"
        className="sheet-dvh w-[min(100vw-2rem,340px)] overflow-y-auto bg-white p-0 pb-[env(safe-area-inset-bottom)] [&>button]:hidden"
        data-track-location="header"
      >
        <div className="p-5">
          <SheetTitle className="sr-only">Navigation menu</SheetTitle>
          <div className="flex items-center justify-between">
            <Logo />
            {/* Radix's default close is a 16px icon; replace it with a 44px target. */}
            <SheetClose asChild>
              <button
                type="button"
                aria-label="Close menu"
                className="flex h-11 w-11 items-center justify-center rounded-lg border border-border text-[#0E1F3A] transition-colors hover:bg-secondary"
                data-testid="mobile-menu-close"
              >
                <X className="h-5 w-5" aria-hidden="true" />
              </button>
            </SheetClose>
          </div>
          {/* SEO audit batch 2, Part 4: the five menu sections as tap-to-expand groups,
              with Call + Request Service as full-width buttons at the bottom. */}
          <div className="mt-4">
            <MobileNav pathname={pathname} />
          </div>
          <div className="mt-5 flex flex-col gap-2">
            <Button asChild variant="outline" className="h-12 w-full border-[#CBD5E1] font-semibold text-[#0E1F3A]" data-testid="mobile-menu-call">
              <a href={COMPANY.phoneHref}>
                <Phone className="mr-1.5 h-4 w-4 text-[#1D4ED8]" aria-hidden="true" /> Call {COMPANY.phone}
              </a>
            </Button>
            <Button asChild className="h-12 w-full bg-[#1D4ED8] font-semibold text-white hover:bg-[#1E40AF]" data-testid="mobile-menu-request-service">
              <Link to="/contact">Request Service</Link>
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  </div>
);

const Header = () => {
  const [condensed, setCondensed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const scrollDirection = useScrollDirection();

  useEffect(() => {
    const onScroll = () => setCondensed(window.scrollY > 12);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setMobileOpen(false), [location.pathname]);

  // Phones only (lg: and up always show the header): slide the header away while the
  // visitor reads downward and bring it back the moment they scroll up.
  const hideOnMobile = scrollDirection === "down" && !mobileOpen;

  return (
    <header
      className={`sticky top-0 z-50 border-b border-border bg-white transition-transform duration-200 will-change-transform lg:!translate-y-0 ${
        condensed ? "shadow-md" : ""
      } ${hideOnMobile ? "header-hide" : ""}`}
      data-testid="site-header"
      data-hidden={hideOnMobile ? "true" : "false"}
    >
      <div
        className={`mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-2 sm:px-6 lg:px-8 transition-[padding] duration-200 ${
          condensed ? "lg:py-2" : "lg:py-4"
        }`}
      >
        <Logo compactOnDesktop />
        <MainNav />
        <DesktopActions />
        <MobileActions open={mobileOpen} onOpenChange={setMobileOpen} pathname={location.pathname} />
      </div>
    </header>
  );
};

// localStorage here holds ONLY this non-sensitive UI flag (promo dismissed).
// Admin auth never touches localStorage — sessions are httpOnly cookies set
// by the backend (see components/admin/api.js).
//
// The key carries the promo id, so a dismissal is remembered for THIS promo on
// every page and every visit, and a new/changed promo (new id) shows again.
// After the promo's end date the banner is not rendered at all.
// localStorage holds only this non-sensitive UI preference ("1" = the visitor dismissed
// the promo strip). No personal data or credentials are ever stored in web storage;
// the admin session uses an httpOnly cookie.
const PROMO_STORAGE_KEY = `idb-promo-dismissed:${PROMO.id}`;

/** True once the promo's last day (local time) has passed. */
const promoExpired = (now = new Date()) => {
  if (!PROMO.endsISO) return false;
  const end = new Date(`${PROMO.endsISO}T23:59:59`);
  return Number.isFinite(end.getTime()) && now > end;
};

const PromoBanner = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (promoExpired()) return;
    try {
      if (localStorage.getItem(PROMO_STORAGE_KEY) !== "1") setVisible(true);
    } catch (err) {
      devWarn("Promo banner: localStorage unavailable, showing banner:", err?.message || err);
      setVisible(true);
    }
  }, []);

  const dismiss = () => {
    setVisible(false);
    try {
      localStorage.setItem(PROMO_STORAGE_KEY, "1");
    } catch (err) {
      // storage unavailable — banner simply reappears next visit
      devWarn("Promo banner: could not persist dismissal:", err?.message || err);
    }
  };

  if (!visible) return null;
  return (
    <div className="relative overflow-hidden bg-[#1D4ED8] text-white" data-testid="promo-banner">
      <div className="band-dots absolute inset-0" aria-hidden="true" />
      <div className="relative mx-auto flex max-w-7xl items-center justify-center gap-2 px-3 py-1.5 pr-12 sm:gap-3 sm:px-6 sm:py-2.5 lg:px-8">
        <BadgePercent className="hidden h-4 w-4 shrink-0 sm:block" aria-hidden="true" />
        <div className="min-w-0">
          <p className="truncate text-xs font-semibold sm:text-sm">
            <span className="sm:hidden">{PROMO.headline}</span>
            <span className="hidden sm:inline">
              Limited time: <span className="font-bold">{PROMO.headline}</span>
              <span className="font-normal text-blue-100"> — {PROMO.detail}{PROMO.ends ? ` Offer ends ${PROMO.ends}.` : ""}</span>
            </span>
          </p>
          {/* Prompt 2 §7 — one line beneath the offer; same expiry/dismissal as the banner. */}
          <p className="truncate text-[11px] text-blue-100 sm:text-xs" data-testid="promo-banner-financing">
            {PROMO_FINANCING_LINE.prefix}
            <FinancingLink placement="promo-card" className="font-semibold text-white underline decoration-blue-300 underline-offset-2 transition-colors hover:text-blue-100">
              {PROMO_FINANCING_LINE.link}
            </FinancingLink>
          </p>
        </div>
        <Link
          to={PROMO.href}
          className="-my-3.5 flex shrink-0 items-center py-3.5 text-xs font-bold underline decoration-blue-300 underline-offset-4 transition-colors hover:text-blue-100 sm:text-sm"
          data-testid="promo-banner-link"
        >
          Learn More
        </Link>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss offer banner"
          data-testid="promo-banner-dismiss"
          className="absolute right-0 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-md text-blue-100 transition-colors hover:bg-white/10 hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-white sm:right-1"
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
};

/**
 * Sticky phone action bar (<768px) — SEO audit batch 2, Part 4: three equal
 * buttons on every page except /review and /contact: Call (tel:), "Text a photo"
 * (sms:) and Estimate (/estimate). ~56px tall (44px buttons + 6px padding) plus
 * the phone's bottom safe area; the footer carries matching bottom padding so
 * the bar never covers content. Each tap pushes one dataLayer event
 * (phone_click / text_click / cta_estimate_click, location "sticky_bar") via
 * the delegated click tracker in lib/analytics.js (batch 3, Part 5).
 */
const STICKY_BAR_HIDDEN_ON = ["/review", "/contact"];
const stickyBarHidden = (pathname) => STICKY_BAR_HIDDEN_ON.some((p) => pathname === p || pathname.startsWith(`${p}/`));

const MobileBottomBar = () => {
  const { pathname } = useLocation();
  if (stickyBarHidden(pathname)) return null;
  return (
    <div
      className="fixed inset-x-0 bottom-0 z-50 grid grid-cols-3 gap-2 border-t border-border bg-white px-3 py-1.5 pb-[calc(env(safe-area-inset-bottom)+0.375rem)] md:hidden"
      data-testid="mobile-bottom-bar"
      data-variant="default"
    >
      <Button asChild variant="outline" className="h-11 border-[#CBD5E1] font-semibold text-[#0E1F3A]" data-testid="mobile-bottom-call-button">
        <a href={COMPANY.phoneHref}>
          <Phone className="mr-1.5 h-4 w-4 text-[#1D4ED8]" aria-hidden="true" /> Call
        </a>
      </Button>
      <Button asChild variant="outline" className="h-11 border-[#CBD5E1] px-2 font-semibold text-[#0E1F3A]" data-testid="mobile-bottom-text-button">
        <a href={COMPANY.smsHref}>
          <MessageSquare className="mr-1.5 h-4 w-4 shrink-0 text-[#1D4ED8]" aria-hidden="true" /> Text a photo
        </a>
      </Button>
      <Button asChild className="h-11 bg-[#1D4ED8] font-semibold text-white hover:bg-[#1E40AF]" data-testid="mobile-bottom-estimate-button">
        <Link to="/estimate" data-cta="estimate">
          <Calculator className="mr-1.5 h-4 w-4" aria-hidden="true" /> Estimate
        </Link>
      </Button>
    </div>
  );
};

const FooterBrandCol = () => (
  <div className="max-md:pb-8">
    <Logo dark />
    <p className="mt-4 max-w-xs text-sm leading-relaxed text-slate-300">{COMPANY.brandStatement}</p>
    <p className="mt-4 inline-flex items-center gap-2 text-xs font-semibold text-blue-300">
      <ShieldCheck className="h-4 w-4" aria-hidden="true" /> {COMPANY.legalLine}
    </p>
    <a
      href={`mailto:${COMPANY.email}`}
      className="tap-row mt-3 flex items-center gap-2 text-sm text-slate-300 hover:text-white"
      data-testid="footer-email-link"
    >
      <Mail className="h-4 w-4 text-blue-400" aria-hidden="true" /> {COMPANY.email}
    </a>
    <a
      href={COMPANY.smsHref}
      className="tap-row mt-2 flex items-center gap-2 text-sm text-slate-300 hover:text-white"
      data-testid="footer-sms-link"
    >
      <MessageSquare className="h-4 w-4 text-blue-400" aria-hidden="true" /> Text us a photo: {COMPANY.phone}
    </a>
  </div>
);

const FOOTER_HEADING = "font-condensed text-xs font-semibold uppercase tracking-[0.2em] text-slate-400";

/**
 * Footer column that collapses on phones (<768px): the heading becomes a 48px
 * toggle, rows stay 44px (.tap-row), closed by default. Content is hidden with
 * CSS max-height - never conditionally rendered - so every footer link stays in
 * the prerendered HTML for crawlers (Search Console Prompt 01). From md: up the
 * heading is static text and the column renders exactly as before.
 */
const FooterCollapsible = ({ id, title, children, testId }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="max-md:border-t max-md:border-white/10" data-testid={testId}>
      <h3 className={FOOTER_HEADING}>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-controls={id}
          className={`flex min-h-12 w-full items-center justify-between gap-3 text-left md:hidden ${FOOTER_HEADING}`}
          data-testid={`${testId}-toggle`}
        >
          {title}
          <ChevronDown className={`h-4 w-4 text-slate-300 transition-transform duration-200 ${open ? "rotate-180" : ""}`} aria-hidden="true" />
        </button>
        <span className="hidden md:inline">{title}</span>
      </h3>
      <div
        id={id}
        className={`overflow-hidden transition-[max-height] duration-300 md:max-h-none md:overflow-visible ${open ? "max-h-[100rem]" : "max-h-0"}`}
        data-expanded={open ? "true" : "false"}
      >
        {children}
      </div>
    </div>
  );
};

const FooterServicesCol = () => (
  <FooterCollapsible id="footer-services-list" title="Services" testId="footer-services-col">
    <ul className="mt-5 space-y-0 md:space-y-2.5 max-md:pb-3">
      {SERVICES.map((s) => (
        <li key={s.slug}>
          <Link to={`/services/${s.slug}`} className="tap-row link-anim text-sm text-slate-300 hover:text-white md:inline" data-testid={`footer-service-${s.slug}`}>
            {s.shortName}
          </Link>
        </li>
      ))}
      <li className="pt-1">
        <Link to="/services" className="tap-row link-anim text-sm font-semibold text-white hover:text-blue-300 md:inline" data-testid="footer-all-services-link">
          All services →
        </Link>
      </li>
    </ul>
  </FooterCollapsible>
);

const FooterOfficesCol = () => (
  <FooterCollapsible id="footer-offices-list" title="Where We Work" testId="footer-offices-col">
    <ul className="mt-5 space-y-5 max-md:pb-4">
      {OFFICES.map((o) => (
        <li key={o.slug} className="text-sm">
          <Link to={`/locations/${o.slug}`} className="tap-target font-semibold text-white hover:text-blue-300" data-testid={`footer-location-${o.slug}`}>
            {o.label}
          </Link>
          <p className="mt-1 text-slate-400">{o.address}</p>
          <a href={o.phoneHref} className="tap-target text-slate-300 hover:text-white">{o.phone}</a>
          {/* Hours belong to the Federal Way office only; service areas have none to post. */}
          {!o.isServiceArea && <p className="text-xs text-slate-500">Mon–Fri 7–6 · Sat 8–4 · 24/7 Emergency</p>}
        </li>
      ))}
    </ul>
  </FooterCollapsible>
);

// ---------------------------------------------------------------------------
// Service Areas block — every city page + the three location hubs, on every
// page of the site. This is the primary crawl path into the 41 local pages
// (Search Console Prompt 01). Links are plain <a href> in the prerendered
// HTML; on phones the grid is collapsed with CSS only, never lazy-rendered.
// City ordering is shared with the Locations menu (src/data/nav.js).
// ---------------------------------------------------------------------------
const footerCityLinks = (countyId) => cityLinksByCounty(countyId, { federalWay: "after-auburn", testIdPrefix: "footer-area" });

const FOOTER_ALSO_SERVING = [
  ...NAV_LOCATIONS.alsoServing.map((a) => ({ key: a.key, to: a.to, label: a.label, testId: `footer-area-${a.key}` })),
  { key: "all", to: "/locations", label: "View all service areas", testId: "footer-area-all", strong: true },
];

const FooterAreaList = ({ items, twoColumns = false }) => (
  <ul className={`mt-3 space-y-0 md:space-y-1.5 ${twoColumns ? "columns-2 gap-x-6" : ""}`}>
    {items.map((i) => (
      <li key={i.key} className="break-inside-avoid">
        <Link
          to={i.to}
          className={`tap-row link-anim text-sm md:inline ${i.strong ? "font-semibold text-white hover:text-blue-300" : "text-slate-300 hover:text-white"}`}
          data-testid={i.testId}
        >
          {i.label}
        </Link>
      </li>
    ))}
  </ul>
);

const FooterServiceAreas = () => {
  const [open, setOpen] = useState(false);
  return (
    <section className="mt-8 border-t border-white/10 pt-6 md:mt-12 md:pt-10" aria-labelledby="footer-service-areas-heading" data-testid="footer-service-areas">
      <div className="flex items-center justify-between gap-4">
        <h3 id="footer-service-areas-heading" className="font-condensed text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">
          Service Areas
        </h3>
        {/* Phones: toggle visibility with CSS only — the links below are always in the HTML. */}
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-controls="footer-service-areas-grid"
          className="inline-flex min-h-11 items-center gap-1 text-sm font-semibold text-white md:hidden"
          data-testid="footer-service-areas-toggle"
        >
          {open ? "Hide" : "Show"} {CITIES.length + OFFICES.length} areas
          <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} aria-hidden="true" />
        </button>
      </div>
      <div
        id="footer-service-areas-grid"
        className={`grid gap-x-10 gap-y-8 overflow-hidden transition-[max-height] duration-300 md:mt-2 md:max-h-none md:grid-cols-2 lg:grid-cols-4 ${
          open ? "mt-2 max-h-[200rem]" : "max-h-0 md:max-h-none"
        }`}
        data-testid="footer-service-areas-grid"
        data-expanded={open ? "true" : "false"}
      >
        {COUNTIES.map((county) => (
          <div key={county.id} data-testid={`footer-areas-${county.id}`}>
            <h4 className="text-sm font-semibold text-white">
              <Link to={`/locations#${county.id}`} className="tap-target hover:text-blue-300" data-testid={`footer-county-${county.id}`}>
                {county.name}
              </Link>
            </h4>
            <FooterAreaList items={footerCityLinks(county.id)} twoColumns={county.id === "king"} />
          </div>
        ))}
        <div data-testid="footer-areas-also-serving">
          <h4 className="text-sm font-semibold text-white">Also serving</h4>
          <FooterAreaList items={FOOTER_ALSO_SERVING} />
        </div>
      </div>
    </section>
  );
};

const FooterQuickLinksCol = () => (
  <div>
    <FooterCollapsible id="footer-quick-links-list" title="Quick Links" testId="footer-quick-links-col">
    <ul className="mt-5 space-y-0 md:space-y-2.5 max-md:pb-3">
      {[
        ["/about", "About Us"], ["/estimate", "Cost Estimator"], ["/pricing", "Pricing Guide"], ["/financing", "Financing"], ["/projects", "Our Work"], ["/reviews", "Reviews"],
        ["/faq", "FAQ"], ["/blog", "Blog & Resources"], ["/contact", "Request Service"],
      ].map(([to, label]) => (
        <li key={to}>
          <Link to={to} className="tap-row link-anim text-sm text-slate-300 hover:text-white md:inline" data-testid={`footer-link${to.replace("/", "-")}`}>
            {label}
          </Link>
        </li>
      ))}
    </ul>
    </FooterCollapsible>
    {/* Social icons stay visible on phones (outside the collapsed list). */}
    <div className="mt-6 flex gap-3 max-md:mt-4" data-testid="footer-social-links">
      {SOCIAL_LINKS.map(({ id, label, href }) => {
        const Icon = SOCIAL_ICONS[id];
        return (
          <a
            key={id}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`${COMPANY.name} on ${label} (opens in a new tab)`}
            title={label}
            className="flex h-11 w-11 items-center justify-center rounded-lg bg-white/10 text-slate-300 transition-colors duration-150 hover:bg-[#1D4ED8] hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#1D4ED8] md:h-9 md:w-9"
            data-testid={`footer-social-${id}`}
          >
            <Icon className="h-4 w-4" aria-hidden="true" />
          </a>
        );
      })}
    </div>
  </div>
);

const FooterBottomRows = () => (
  <>
    <Separator className="my-6 bg-white/10 md:my-8" />
    <div className="flex flex-col items-center justify-between gap-3 text-xs text-slate-400 sm:flex-row" data-testid="footer-family-row">
      <p>
        Part of the ID Builder family:{" "}
        {FAMILY_BRANDS.map((b, i) => (
          <React.Fragment key={b.name}>
            {i > 0 && <span aria-hidden="true"> · </span>}
            <a
              href={b.href}
              target="_blank"
              rel="noopener noreferrer"
              className="tap-target font-semibold text-slate-300 hover:text-white"
              data-testid={`footer-family-${b.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            >
              {b.name}
            </a>
          </React.Fragment>
        ))}
      </p>
    </div>
    <p className="mt-2 text-center text-xs text-slate-400 sm:text-left" data-testid="footer-family-line">
      Part of the ID Builder family of Washington companies.
    </p>
    <Separator className="my-6 bg-white/10 md:my-8" />
    <div className="flex flex-col items-center justify-between gap-3 text-xs text-slate-400 sm:flex-row">
      <p>© {new Date().getFullYear()} {COMPANY.name}. All rights reserved.</p>
      <div className="flex gap-6">
        <Link to="/privacy" className="tap-target hover:text-white max-md:min-h-11" data-testid="footer-privacy-link">Privacy Policy</Link>
        <Link to="/site-map" className="tap-target hover:text-white max-md:min-h-11" data-testid="footer-sitemap-link">Sitemap</Link>
      </div>
    </div>
  </>
);

const Footer = () => (
  <footer className="grain-overlay band-blueprint relative overflow-hidden bg-[#0A1628] text-white" data-testid="site-footer">
    <span className="absolute left-4 top-0 h-[3px] w-16 bg-[#1D4ED8] sm:left-6 lg:left-8" aria-hidden="true" />
    {/* Extra bottom padding on phones keeps the last rows clear of the fixed action bar. */}
    <div className="relative mx-auto max-w-7xl px-4 pt-16 pb-[calc(6rem+env(safe-area-inset-bottom))] sm:px-6 md:pb-16 lg:px-8">
      <div className="grid gap-10 max-md:gap-0 md:grid-cols-2 lg:grid-cols-4">
        <FooterBrandCol />
        <FooterServicesCol />
        <FooterOfficesCol />
        <FooterQuickLinksCol />
      </div>
      <FooterServiceAreas />
      <FooterBottomRows />
    </div>
  </footer>
);

const ScrollToTop = () => {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      // Let the new route paint, then jump to the anchored section (offset for the sticky header).
      const id = hash.slice(1);
      const t = window.setTimeout(() => {
        const el = document.getElementById(id);
        if (el) {
          const top = el.getBoundingClientRect().top + window.scrollY - 96;
          window.scrollTo({ top, behavior: "instant" });
        } else {
          window.scrollTo({ top: 0, behavior: "instant" });
        }
      }, 60);
      return () => window.clearTimeout(t);
    }
    window.scrollTo({ top: 0, behavior: "instant" });
    return undefined;
  }, [pathname, hash]);
  return null;
};

export default function Layout() {
  return (
    <ReviewsProvider>
    <div className="App flex min-h-screen flex-col bg-background">
      {/* The single business entity (@id #plumber), on every page; all other JSON-LD refers to it. */}
      <JsonLd data={PLUMBER_SCHEMA} />
      <ScrollToTop />
      <EmergencyBar />
      <Header />
      <PromoBanner />
      <main className="flex-1">
        {/* Route chunks are lazy (src/AppRoutes.jsx); this boundary keeps header/footer in place while one loads. */}
        <Suspense fallback={<RouteFallback />}>
          <Outlet />
        </Suspense>
      </main>
      <Footer />
      <MobileBottomBar />
      <Toaster position="top-center" richColors />
    </div>
    </ReviewsProvider>
  );
}
